import { Control } from '@Common/Controls';
import { errorDialog } from '@Common/Dialogs/Error';
import { InsightExceptionType, InsightVersionCheckResultType } from '@Common/Enums/Enum';
import { HtmlError, InsightVersionCheckResult } from '@InstanceFactory';
import { properties } from '../Core/Properties';
import * as commonUtils from './CommonUtils';
import { ServerError } from './Errors';
import urlHelper from './UrlHelper';

namespace errorHelper {
    export function attachErrorHandling(): void {
        if (properties.isPrint) {
            window.onerror = errorHelper.errorHandler;
        }
    }

    export function errorHandler(): boolean {
        const result = {};
        _.forEach(arguments, (item, index) => {
            result['_' + index] = item;
        });

        commonUtils.log(result);
        errorHelper.errorOccurs({ error: JSON.stringify(result) });

        return true;
    }

    export function getError(error: Error | JQueryXHR): Error | HtmlError {
        if (error instanceof Error) {
            return error;
        } else {
            return parseError(error);
        }
    }

    export function parseError(error: JQueryXHR): HtmlError {
        try {
            if (error != null) {
                return HtmlError.create(JSON.parse(error.responseText));
            } else {
                return HtmlError.create({ message: '', text: '', type: '' });
            }
        } catch (e) {
            return HtmlError.create({
                message: error.statusText + '(' + error.status + ')',
                text: getServerErrorText(error),
                type: ''
            });
        }
    }

    const getServerErrorText = (error: JQueryXHR): string => {
        try {
            const response = (error.responseText ? error.responseText : error.responseXML)?.toString();
            if (response != null && properties.isServerDebug) {
                let text = response.toLowerCase();
                const startIndex = text.indexOf('<body>');
                const endIndex = text.indexOf('</body>');

                if (startIndex !== -1 && endIndex !== -1) {
                    text = response.substring(startIndex, endIndex);
                } else {
                    text = response;
                }
                return $(text).text() ?? '';
            }
        } catch (ex) {}
        return '';
    };

    // for testing
    export function errorOccurs(error: any): void {
        if (properties.isPrint && error) {
            errorHelper.log(commonUtils.objectToString(error));
        }
    }

    export function processError(message: Nullable<string>, error: JQueryXHR, dontShowDialog: boolean = false): HtmlError {
        const htmlError = errorHelper.parseError(error);

        commonUtils.log(htmlError);
        errorHelper.errorOccurs(htmlError);

        const applicationDisabled = htmlError.type === InsightExceptionType.applicationDisabled;
        if (!this.applicationDisabledDialog && applicationDisabled) {
            this.applicationDisabledDialog = true;
            errorHelper.showError(htmlError.message, '', '', () => urlHelper.logout());
            return htmlError;
        }

        const isSessionExpired = htmlError.type === InsightExceptionType.unknownSession;
        if (!this.isSessionExpiredDialogVisible || !isSessionExpired) {
            if (isSessionExpired || (error && error.status === 502)) {
                if (altosoft.reloadOnSessionExpired) {
                    urlHelper.refresh({ reloadOnSessionExpired: '1' });
                } else {
                    this.isSessionExpiredDialogVisible = true;
                    errorHelper.showError(isSessionExpired ? strings.sessionExpiredMessage : htmlError.message, '', '', () => {
                        this.isSessionExpiredDialogVisible = false;
                        urlHelper.logout(isSessionExpired);
                    });
                }
            } else if (!dontShowDialog) {
                errorHelper.showException(message, htmlError);
            }
        }

        return htmlError;
    }

    export function showException(error: ServerError): Promise<void>;
    export function showException(message: Nullable<string>, htmlError: HtmlError): Promise<void>;
    export function showException(...args: [ServerError] | [Nullable<string>, HtmlError]): Promise<void> {
        const [message, htmlError] = args.length === 1 ? [args[0].message, args[0].error] : args;
        let exMessage = '';
        let errorText = htmlError.text;
        if (htmlError.type === InsightExceptionType.serviceUnavailable) {
            exMessage = strings.serverExceptions.serviceUnavailable;
            errorText = '';
        } else if (htmlError.message) {
            exMessage = message ? message + '\n' + htmlError.message : htmlError.message;
        } else {
            exMessage = message ?? '';
        }
        return new Promise(resolve => errorHelper.showError(exMessage, errorText == htmlError.message ? '' : errorText, undefined, resolve));
    }

    export function showError(message: string, errorMessage?: string | JQuery | Control, errorBody?: string, callback?: () => void): void {
        if (urlHelper.canShowError !== false) {
            urlHelper.canShowError = false;
            const dialog = errorDialog({
                message: message,
                exceptionMessage: errorMessage,
                exceptionBody: errorBody
            });
            dialog.cancel(() => {
                urlHelper.canShowError = true;
                if (_.isFunction(callback)) {
                    callback();
                }
            });
            dialog.show();
        }
    }

    export function writeWarning(message: string): void {
        if (properties.isDebug && message) {
            commonUtils.log('Warning: ' + message);
        }
    }

    export function log(e: Error | string): void {
        // tslint:disable-next-line: no-console
        console.error(e);
    }

    export function attempt<T>(handler: IFunction<T>, defaultValue?: T): T;
    export function attempt(handler: IHandler): void;
    export function attempt<T>(handler: IHandler | IFunction<T>, defaultValue?: any): any | void {
        try {
            return handler();
        } catch (e) {
            log(e);
            return defaultValue;
        }
    }

    const isFatalTypes = ['NotSupportedFixPack', 'NotSupportedVersion'] as InsightVersionCheckResultType[];
    const template = _.cond<InsightVersionCheckResultType, string>([
        [_.matches(InsightVersionCheckResultType.notSupportedFixPack), _.constant(strings.importProject.analyticNotSupportedFixPackFatal)],
        [_.matches(InsightVersionCheckResultType.notSupportedVersion), _.constant(strings.importProject.analyticNotSupportedVersionFatal)],
        [_.stubTrue, _.constant(strings.importProject.analyticUnconfirmedSupport)]
    ]);
    export const getInsightVersionCheckResult = _.cond<InsightVersionCheckResult, { isFatal: boolean; message: string } | null>([
        [_.isNull, _.constant(null)],
        [_.matches({ resultType: InsightVersionCheckResultType.supportedVersion }), _.constant(null)],
        [
            _.stubTrue,
            ({ resultType, currentVersion, fixPackVersion }) => ({
                isFatal: isFatalTypes.includes(resultType),
                message: template(resultType).format(currentVersion, fixPackVersion)
            })
        ]
    ]);
}

export default errorHelper;
